import { createContext, useContext, type ReactNode, useState } from "react";

type Id = string | number;

interface OverrideById {
  id: Id;
  value: string;
}

type Override = OverrideById;

interface State {
  overrides: Record<Id, Override>;
}

interface Value {
  state: State;
  setOverrideById(id: Id, value: string): void;
  getOverrideById(id: Id): Override | null;
  deleteOverrideById(id: Id): void;
}

const BreadcrumbsContext = createContext<Value | null>(null);

type BreadcrumbsProviderProps = {
  children: ReactNode;
};

export function BreadcrumbsProvider(
  props: React.PropsWithChildren<BreadcrumbsProviderProps>,
) {
  const [state, setState] = useState<State>({
    overrides: {},
  });

  function setOverrideById(index: Id, value: string) {
    setState((prevState) => {
      return {
        ...prevState,
        overrides: {
          ...prevState.overrides,
          [index]: {
            id: index,
            value,
          },
        },
      };
    });
  }

  function getOverrideById(id: Id) {
    return state.overrides[id] || null;
  }

  function deleteOverrideById(id: Id) {
    setState((prevState) => {
      const newState = { ...prevState };
      delete newState.overrides[id];
      return newState;
    });
  }

  return (
    <BreadcrumbsContext.Provider
      value={{
        state,
        setOverrideById,
        getOverrideById,
        deleteOverrideById,
      }}
    >
      {props.children}
    </BreadcrumbsContext.Provider>
  );
}

export function useBreadcrumbsContext() {
  const context = useContext(BreadcrumbsContext);

  if (!context) {
    throw new Error("useBreadcrumbs must be used within a BreadcrumbsProvider");
  }

  return context;
}
